import React from "react";
import { ProductChangeCandidate } from "../../interfaces/sales";

export interface ProductChangeSelectionStepProps {
  candidates: ProductChangeCandidate[];
  selectedProducts: string[];
  onToggleProduct: (uuidSaleProduct: string) => void;
}

/**
 * YOSHI-10261: step 1 of the product change. Lists the registered products and lets one or several
 * be selected. A product without valid options is shown disabled with the reason. Each addon is
 * listed under the product it hangs from, the same way the partial cancellation lists it.
 */
export const ProductChangeSelectionStep: React.FC<
  ProductChangeSelectionStepProps
> = ({ candidates, selectedProducts, onToggleProduct }) => {
  const saleProductUUIDs: Set<string> = new Set(
    candidates.map((candidate) => candidate.uuid_sale_product),
  );
  const isAddon = (candidate: ProductChangeCandidate): boolean =>
    !!candidate.uuid_parent && saleProductUUIDs.has(candidate.uuid_parent);

  const parentCandidates: ProductChangeCandidate[] = candidates.filter(
    (candidate) => !isAddon(candidate),
  );

  const getAddons = (uuidSaleProduct: string): ProductChangeCandidate[] =>
    candidates.filter(
      (candidate) =>
        isAddon(candidate) && candidate.uuid_parent === uuidSaleProduct,
    );

  const renderCandidateRow = (
    candidate: ProductChangeCandidate,
    isAddonRow: boolean,
  ) => {
    const isSelected: boolean = selectedProducts.includes(
      candidate.uuid_sale_product,
    );
    const hasOptions: boolean = candidate.options.length > 0;

    return (
      <button
        key={candidate.uuid_sale_product}
        type="button"
        disabled={!hasOptions}
        onClick={() => onToggleProduct(candidate.uuid_sale_product)}
        className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-60 ${
          isAddonRow ? "ml-6 w-[calc(100%-1.5rem)] border-dashed" : "w-full"
        } ${
          isSelected
            ? "border-indigo-200 bg-indigo-50"
            : "border-gray-200 bg-white hover:bg-gray-50"
        }`}
      >
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm font-medium ${
              isAddonRow ? "text-gray-600" : "text-gray-900"
            }`}
          >
            {isAddonRow && "↳ "}
            {candidate.product} × {candidate.quantity}
          </p>
          <p className="text-xs text-gray-500">
            ${candidate.price.toFixed(2)} · {candidate.name_concession}
          </p>
          {!hasOptions && (
            <p className="mt-1 text-xs text-amber-600">
              {isAddonRow
                ? "Sin otro producto de su mismo complemento y del mismo precio"
                : "Sin productos de reemplazo de la misma concesión y del mismo precio"}
            </p>
          )}
        </div>
        <span
          className={`ml-3 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
            isSelected
              ? "border-indigo-600 bg-indigo-600"
              : "border-gray-300 bg-white"
          }`}
        >
          {isSelected && (
            <span className="text-[11px] font-bold text-white">✓</span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Selecciona los productos que se marcaron por error. El reemplazo debe
        ser de la misma concesión y del mismo precio, por lo que el cobro no
        cambia.
      </p>
      <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
        {parentCandidates.map((candidate) => (
          <React.Fragment key={candidate.uuid_sale_product}>
            {renderCandidateRow(candidate, false)}
            {getAddons(candidate.uuid_sale_product).map((addon) =>
              renderCandidateRow(addon, true),
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
